import { StockCategoryFilter } from '../../types/stockCategories';

/**
 * Build a Wordle-style emoji share line with multiple lines (one per guess).
 * Rules:
 * - Max guesses = 4 by default.
 * - Wrong attempt = "🟥" (red square) for most categories, "🦍" (gorilla) for WallStreetBets
 * - Remaining guesses = "⬜" (white square)
 * - Win = "🟩" (green square) for most categories, "🧑‍🚀" (astronaut) for WallStreetBets
 *
 * Examples (non-WallStreetBets):
 *  win at 2  -> "🟥⬜⬜⬜ | ❌\n🟥🟩⬜⬜ | ✅"
 *  fail in 4 -> "🟥⬜⬜⬜ | ❌\n🟥🟥⬜⬜ | ❌\n🟥🟥🟥⬜ | ❌\n🟥🟥🟥🟥 | ❌"
 *
 * Examples (WallStreetBets):
 *  win at 2  -> "🦍⬜⬜⬜ | ❌\n🦍🧑‍🚀⬜⬜ | ✅"
 *  fail in 4 -> "🦍⬜⬜⬜ | ❌\n🦍🦍⬜⬜ | ❌\n🦍🦍🦍⬜ | ❌\n🦍🦍🦍🦍 | ❌"
 */
export function buildShareLine(
  opts:
    | { maxGuesses?: number; winAt: number; category?: StockCategoryFilter }   // 1-based index of the winning guess
    | { maxGuesses?: number; fail: true; category?: StockCategoryFilter }      // explicit failure case
): string {
  const max = ("maxGuesses" in opts && opts.maxGuesses) ? opts.maxGuesses! : 4;
  const category = "category" in opts ? opts.category : undefined;

  // WallStreetBets uses gorilla and astronaut, all other categories use red/green squares
  const isWallStreetBets = category === 'wallstreetbets';
  const WRONG = isWallStreetBets ? "🦍" : "🟥";
  const EMPTY = "⬜";
  const WIN   = isWallStreetBets ? "🧑‍🚀" : "🟩";
  const CORRECT = " | ✅";
  const INCORRECT = " | ❌";

  const lines: string[] = [];

  // Failure case: Generate all max attempts, all ending with INCORRECT
  if ("fail" in opts && opts.fail) {
    for (let i = 1; i <= max; i++) {
      const wrongTiles = WRONG.repeat(i);
      const emptyTiles = EMPTY.repeat(max - i);
      lines.push(`${wrongTiles}${emptyTiles}${INCORRECT}`);
    }
    return lines.join('\n');
  }

  // Win path - TypeScript now knows opts must have winAt
  if ("winAt" in opts) {
    const k = Math.max(1, Math.min(max, opts.winAt)); // clamp to [1, max]

    // Generate lines for attempts before the win (all end with INCORRECT)
    for (let i = 1; i < k; i++) {
      const wrongTiles = WRONG.repeat(i);
      const emptyTiles = EMPTY.repeat(max - i);
      lines.push(`${wrongTiles}${emptyTiles}${INCORRECT}`);
    }

    // Generate the winning line (ends with CORRECT)
    const wrongTiles = WRONG.repeat(k - 1);
    const emptyTiles = EMPTY.repeat(max - k);
    lines.push(`${wrongTiles}${WIN}${emptyTiles}${CORRECT}`);

    return lines.join('\n');
  }

  // This should never happen, but TypeScript requires a return
  return WRONG.repeat(max) + INCORRECT;
}
